import { LoadingOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Input, Modal, Space, Typography, message } from 'antd';
import React, { useState } from 'react';

const { Text } = Typography;

interface DeepResearchModalProps {
  visible: boolean;
  onCancel: () => void;
  onResearch: (config: ResearchConfig) => void;
  email: string;
  name: string
}

interface ResearchConfig {
  focusAreas: string[];
  depth: 'basic' | 'std' | 'adv';
}

const QUICK_ADD_OPTIONS = [
  'Find company details',
  'Decision makers & contacts',
  'Recent company news',
  'Tech stack & tools',
  'Pain points & needs',
  'Competitors',
  'Funding & financials'
];

const DEPTH_OPTIONS = [
  { label: 'Basic (2-3 min)', value: 'basic' },
  { label: 'Standard (5-7 min)', value: 'std' },
  { label: 'Advanced (10+ min)', value: 'adv' }
];

const DEPTH_LIMITS = {
  basic: 2,
  std: 4,
  adv: Infinity
};

export const DeepResearchModal: React.FC<DeepResearchModalProps> = ({
  visible,
  onCancel,
  onResearch,
  name
}) => {
  const [focusAreas, setFocusAreas] = useState<string[]>([]);
  const [customFocus, setCustomFocus] = useState('');
  const [depth, setDepth] = useState<'basic' | 'std' | 'adv'>('basic');
  const [isResearching, setIsResearching] = useState(false);

  const getCurrentLimit = () => DEPTH_LIMITS[depth];

  const handleAddFocusArea = (area: string) => {
    const currentLimit = getCurrentLimit();

    if (focusAreas.length >= currentLimit) {
      const limitText = depth === 'adv' ? 'unlimited' : currentLimit;
      const depthText = depth === 'basic' ? 'Basic research' : depth === 'std' ? 'Standard research' : 'Advanced research';
      const upgradeText = depth === 'adv' ? '' : ' Please upgrade to a higher research level for more options.';

      message.warning(
        `Selection Limit Reached\n${depthText} allows a maximum of ${limitText} focus areas.${upgradeText}`,
        5
      );
      return;
    }

    if (!focusAreas.includes(area)) {
      setFocusAreas([...focusAreas, area]);
    }
  };

  const handleAddCustomFocus = () => {
    if (customFocus.trim().length < 50) {
      message.error('Research focus must be at least 50 characters long.');
      return;
    }

    const currentLimit = getCurrentLimit();

    if (focusAreas.length >= currentLimit) {
      const limitText = depth === 'adv' ? 'unlimited' : currentLimit;
      const depthText = depth === 'basic' ? 'Basic research' : depth === 'std' ? 'Standard research' : 'Advanced research';
      const upgradeText = depth === 'adv' ? '' : ' Please upgrade to a higher research level for more options.';

      message.warning(
        `Selection Limit Reached\n${depthText} allows a maximum of ${limitText} focus areas.${upgradeText}`,
        5
      );
      return;
    }

    if (customFocus.trim() && !focusAreas.includes(customFocus.trim())) {
      setFocusAreas([...focusAreas, customFocus.trim()]);
      setCustomFocus('');
    }
  };

  const handleRemoveFocusArea = (area: string) => {
    setFocusAreas(focusAreas.filter(f => f !== area));
  };

  const handleResearch = () => {
    if (focusAreas.length === 0) return;

    setIsResearching(true);
    onResearch({ focusAreas, depth });

    // Simulate research process
    setTimeout(() => {
      setIsResearching(false);
      onCancel();
    }, 3000);
  };

  const handleDepthChange = (newDepth: 'basic' | 'std' | 'adv') => {
    setDepth(newDepth);
    // Clear focus areas if new limit is lower than current selection
    const newLimit = DEPTH_LIMITS[newDepth];
    if (focusAreas.length > newLimit) {
      setFocusAreas(focusAreas.slice(0, newLimit));
      message.info(`Research depth changed to ${newDepth}. Focus areas adjusted to fit the new limit.`);
    }
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div
            style={{
              width: '40px',
              height: '40px',
              backgroundColor: '#e6f7ff',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <SearchOutlined style={{ fontSize: '20px', color: '#1890ff' }} />
          </div>
          <div>
            <Text strong style={{ fontSize: '18px' }}>Deep Research Configuration</Text><br />
            <Text type='secondary' style={{ fontSize: '14px' }}>Research for: {name}</Text>
          </div>
        </div>
      }
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={800}
      centered
    >
      <div style={{ padding: '24px 0' }}>
        {/* What should the research focus on? */}
        <div style={{ marginBottom: '32px' }}>
          <Text strong style={{ display: 'block', marginBottom: '16px', fontSize: '16px' }}>
            What should the research focus on?
          </Text>
          <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', gap: '12px' }}>
              <Input
                placeholder="E.g., Find decision makers, Check recent funding, Analyze company growth.. (minimum 50 characters)"
                value={customFocus}
                onChange={(e) => setCustomFocus(e.target.value)}
                onPressEnter={handleAddCustomFocus}
                style={{ flex: 1 }}
                status={customFocus.length > 0 && customFocus.length < 50 ? 'error' : ''}
              />
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddCustomFocus}
                disabled={customFocus.length < 50}
              >
                Add
              </Button>
            </div>
            {customFocus.length > 0 && customFocus.length < 50 && (
              <Text type="danger" style={{ fontSize: '12px' }}>
                Research focus must be at least 50 characters long. ({customFocus.length}/50)
              </Text>
            )}
          </div>


          {/* Selected focus areas */}
          {focusAreas.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <Space wrap>
                {focusAreas.map((area, index) => (
                  <Button
                    key={index}
                    size="small"
                    onClick={() => handleRemoveFocusArea(area)}
                    style={{ borderRadius: '16px' }}
                  >
                    {area} ×
                  </Button>
                ))}
              </Space>
            </div>
          )}
        </div>

        {/* Quick add section */}
        <div style={{ marginBottom: '32px' }}>
          <Text strong style={{ display: 'block', marginBottom: '16px', fontSize: '16px' }}>
            Quick add:
          </Text>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
            {QUICK_ADD_OPTIONS.map((option) => (
              <Button
                key={option}
                size="small"
                icon={<PlusOutlined />}
                onClick={() => handleAddFocusArea(option)}
                disabled={focusAreas.includes(option) || focusAreas.length >= getCurrentLimit()}
                style={{ borderRadius: '16px' }}
              >
                {option}
              </Button>
            ))}
          </div>
        </div>

        {/* Research depth */}
        <div style={{ marginBottom: '32px' }}>
          <Text strong style={{ display: 'block', marginBottom: '16px', fontSize: '16px', color: '#333' }}>
            Research depth:
          </Text>
          <div style={{
            display: 'flex',
            gap: '4px',
            overflow: 'hidden'
          }}>
            {DEPTH_OPTIONS.map((option, index) => (
              <Button
                key={option.value}
                type={depth === option.value ? 'primary' : 'default'}
                onClick={() => handleDepthChange(option.value as 'basic' | 'std' | 'adv')}
                style={{
                  flex: 1,
                  border: depth === option.value ? '1px solid #1890ff' : '1px solid #d9d9d9',
                  borderRadius: '6px',
                  color: depth === option.value ? '#1890ff' : '#000000',
                  backgroundColor: 'transparent',
                  height: '40px',
                  fontSize: '14px',
                  fontWeight: depth === option.value ? '500' : '400'
                }}
              >
                {option.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderTop: '1px solid #f0f0f0',
          paddingTop: '20px',
          marginTop: '8px'
        }}>
          <Text type="secondary">
            {focusAreas.length === 0 ? 'Add at least one focus area' : `${focusAreas.length} focus area(s) selected${depth !== 'adv' ? ` (max: ${getCurrentLimit()})` : ''}`}
          </Text>
          <Space>
            <Button onClick={onCancel}>Cancel</Button>
            <Button
              type="primary"
              disabled={focusAreas.length === 0 || isResearching}
              onClick={handleResearch}
              icon={isResearching ? <LoadingOutlined /> : null}
            >
              {isResearching ? 'Researching...' : 'Start Research'}
            </Button>
          </Space>
        </div>
      </div>
    </Modal>
  );
}; 